import { Button, Message } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Location, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LoginInput } from '../../api/auth';
import TextFieldWithControl from '../../components/ui/TextFieldWithControl';
import { loginUserAction } from '../../data/actions/authActions';
import {
  authErrorSelector,
  isAuthenticatedSelector,
  isAuthLoadingSelector,
} from '../../data/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { getEmailRules } from '../../utils/rules';
import LoginLayout from './components/LoginLayout';

const FieldsWrapper = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;

  & > * {
    margin-top: 1rem;
  }
`;

export interface LocationWithState extends Omit<Location, 'state'> {
  state: {
    from?: string;
  };
}

const LoginView = () => {
  const { t } = useTranslation();
  const location = useLocation() as LocationWithState;
  const dispatch = useAppDispatch();

  const errors = useAppSelector(authErrorSelector);
  const isLoading = useSelector(isAuthLoadingSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const { control, handleSubmit } = useForm<LoginInput>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (values: LoginInput) => {
    void dispatch(loginUserAction(values));
  };

  if (isAuthenticated) {
    return <Navigate to={location.state?.from ?? '/items'} replace />;
  }

  return (
    <LoginLayout>
      <FieldsWrapper onSubmit={handleSubmit(onSubmit)}>
        {errors?.common && (
          <Message withIcon type="error" fullWidth inverted>
            {errors.common}
          </Message>
        )}
        <TextFieldWithControl
          name="email"
          errors={errors.email}
          placeholder={t('Email')}
          control={control}
          rules={getEmailRules(t)}
        />

        <TextFieldWithControl
          name="password"
          placeholder={t('Password')}
          control={control}
          type="password"
          rules={{
            required: {
              value: true,
              message: t('Password is required'),
            },
          }}
        />

        <Button color="primary" isLoading={isLoading}>
          {t('Log in')}
        </Button>
      </FieldsWrapper>
    </LoginLayout>
  );
};

export default LoginView;
